import React, { useState } from 'react';
import { X, Clipboard, Check, AlertCircle } from 'lucide-react';
import { parseTableText } from '../utils/pasteParser';
import { formatDateToIso, addDays } from '../utils/dateUtils';
import { GanttTask } from '../types/gantt';

interface SmartPasteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (tasks: GanttTask[]) => void;
}

export const SmartPasteModal: React.FC<SmartPasteModalProps> = ({
  isOpen,
  onClose,
  onApply,
}) => {
  const [pasteText, setPasteText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [previewTasks, setPreviewTasks] = useState<GanttTask[]>([]);

  if (!isOpen) return null;

  const handleTextChange = (text: string) => {
    setPasteText(text);
    if (!text.trim()) {
      setError(null);
      setPreviewTasks([]);
      return;
    }

    const res = parseTableText(text);
    if (res.error) {
      setError(res.error);
      setPreviewTasks([]);
    } else {
      setError(null);
      setPreviewTasks(res.tasks);
    }
  };

  const handleApply = () => {
    if (previewTasks.length > 0) {
      onApply(previewTasks);
      onClose();
    }
  };

  const loadExampleMarkdown = () => {
    const today = new Date();
    const d0 = formatDateToIso(today);
    const d1 = addDays(d0, 14);
    const d2 = addDays(d0, 28);
    const d3 = addDays(d0, 42);
    const d4 = addDays(d0, 56);
    const example = `| Activity | Start | End |
|---|---|---|
| Project Plan | ${d0} | ${d1} |
| UI Design | ${addDays(d0, 7)} | ${d2} |
| Development | ${addDays(d0, 15)} | ${d3} |
| QA & Testing | ${addDays(d0, 35)} | ${d4} |
| Launch Milestone | ${d4} | ${d4} |`;
    handleTextChange(example);
  };

  const loadExampleTsv = () => {
    const today = new Date();
    const d0 = formatDateToIso(today);
    const d1 = addDays(d0, 14);
    const d2 = addDays(d0, 30);
    const d3 = addDays(d0, 45);
    const example = `Planning & Discovery\t${d0}\t${d1}\nSprint 1 - Frontend\t${addDays(d0, 10)}\t${d2}\nSprint 2 - Backend\t${addDays(d0, 20)}\t${d3}\nGo-Live\t${d3}\t${d3}`;
    handleTextChange(example);
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container">
        <div className="modal-header">
          <div className="modal-title">
            <Clipboard size={18} />
            <h2>Smart Paste from Excel / Markdown / CSV</h2>
          </div>
          <button onClick={onClose} className="btn-icon">
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          <p className="modal-subtitle">
            Paste your table data below. The parser automatically detects tab-separated Excel rows, Markdown tables, CSVs, and date formats (e.g. <code>1-Sep-2024</code>, <code>2024-09-01</code>, <code>01.09.2024</code>).
          </p>

          <div className="example-buttons">
            <span>Try sample format:</span>
            <button onClick={loadExampleMarkdown} className="btn-link">
              Markdown Table
            </button>
            <button onClick={loadExampleTsv} className="btn-link">
              Excel TSV Format
            </button>
          </div>

          <textarea
            className="paste-textarea"
            placeholder={`| Activity | Start | End |\n| Plan | 1-Sep-2024 | 23-Sep-2024 |\n| Develop | 24-Sep-2024 | 4-Oct-2024 |`}
            value={pasteText}
            onChange={(e) => handleTextChange(e.target.value)}
            rows={8}
            autoFocus
          />

          {error && (
            <div className="alert-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {previewTasks.length > 0 && (
            <div className="parsed-preview">
              <div className="parsed-preview-header">
                <Check size={16} color="#059669" />
                <span>Found {previewTasks.length} valid task(s):</span>
              </div>
              <ul className="parsed-list">
                {previewTasks.map((t, idx) => (
                  <li key={idx}>
                    <strong>{t.activity}</strong>: {t.start} &rarr; {t.end}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={previewTasks.length === 0}
            className="btn btn-primary"
          >
            Import {previewTasks.length > 0 ? `(${previewTasks.length} tasks)` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};
